/**
 * WebSocket client for real-time VM status updates.
 *
 * Connects to /ws/vms/status and dispatches status updates via callback.
 * Handles reconnection with exponential backoff.
 */

import type { VmHealthStatus, VmLifecycleState, RdpUser, VmNicAddr } from './types.js';
import { api } from './client.js';
import { vms, setVms } from '../globals.js';
import { renderVmTable } from '../vm/table.js';

// Status message from the WebSocket
export interface MappedPort {
  host_port: number;
  guest_port: number;
  protocol: string;
}

export interface WsVmStatus {
  type: 'vm_status';
  name: string;
  id?: number;
  description?: string;
  running: boolean;
  pid?: number;
  cpus?: number;
  mem_gb?: number;
  enabled?: boolean;
  lifecycle_state?: VmLifecycleState;
  active_connections?: number;
  idle_seconds?: number;
  health?: VmHealthStatus;
  qmp_available?: boolean;
  ip_address?: string;
  nics?: VmNicAddr[];
  uptime_seconds?: number;
  mapped_ports?: MappedPort[];
  rdp_users?: RdpUser[];
  host?: string;
  balloon_total_mb?: number;
  balloon_free_mb?: number;
  balloon_actual_mb?: number;
  cpu_usage?: number;
}

export type VmStatusHandler = (status: WsVmStatus) => void;

const RECONNECT_DELAY_INITIAL = 2000;
const RECONNECT_DELAY_MAX = 30000;

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = RECONNECT_DELAY_INITIAL;
let statusHandler: VmStatusHandler | null = null;
let connectedHandler: (() => void) | null = null;
let stopped = false;

function cleanup(): void {
  if (ws) {
    const old = ws;
    ws = null;
    if (old.readyState === WebSocket.OPEN || old.readyState === WebSocket.CONNECTING) {
      old.close();
    }
  }
}

function scheduleReconnect(): void {
  if (stopped) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, reconnectDelay);
  reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_DELAY_MAX);
}

function connect(): void {
  if (stopped || ws) return;

  const socket = new WebSocket(api.buildWsUrl('/ws/vms/status'));
  ws = socket;
  let opened = false;

  socket.onopen = () => {
    opened = true;
    reconnectDelay = RECONNECT_DELAY_INITIAL;
    // Subscribe to all VMs — the frontend-facing host tracks connection
    // state (active_connections, mapped_ports) for all VMs including
    // delegated ones. The merge logic in updateVmInTable filters out
    // lifecycle/runtime fields for delegated VMs (those come from the
    // remote host's WebSocket instead).
    const ids = vms.map(vm => vm.id);
    socket.send(JSON.stringify({ type: 'subscribe', vm_ids: ids }));
  };

  socket.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'subscribed') {
        if (connectedHandler) {
          connectedHandler();
          connectedHandler = null;
        }
      } else if (data.type === 'vm_status' && statusHandler) {
        statusHandler(data as WsVmStatus);
      } else if (data.type === 'vm_deleted') {
        const idx = vms.findIndex(vm => vm.id === data.id);
        if (idx >= 0) {
          vms.splice(idx, 1);
          setVms(vms);
          renderVmTable(vms);
        }
      }
    } catch {
      // Ignore parse errors
    }
  };

  socket.onclose = () => {
    cleanup();
    if (!opened) {
      // WS upgrade failed — verify session before retrying (may be expired)
      api.verifySession().then(valid => {
        if (valid) scheduleReconnect();
        // If invalid, onAuthError already redirected to login
      });
    } else {
      scheduleReconnect();
    }
  };

  socket.onerror = () => {
    // onclose fires after onerror
  };
}

/**
 * Start the VM status WebSocket connection.
 * Calls onStatus for each VM status update received.
 * Returns a cleanup function to disconnect.
 */
export function connectVmStatus(onStatus: VmStatusHandler, onConnected?: () => void): () => void {
  statusHandler = onStatus;
  connectedHandler = onConnected ?? null;
  stopped = false;
  reconnectDelay = RECONNECT_DELAY_INITIAL;
  connect();

  return disconnectVmStatus;
}

/**
 * Disconnect the VM status WebSocket.
 */
export function disconnectVmStatus(): void {
  stopped = true;
  statusHandler = null;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  cleanup();
  disconnectRemoteHosts();
}

// ============================================================================
// Remote Host Status Connections
// ============================================================================

interface RemoteHostConn {
  host: string;
  ws: WebSocket | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  reconnectDelay: number;
}

const remoteHosts: RemoteHostConn[] = [];

function connectRemoteHost(rh: RemoteHostConn): void {
  if (stopped || rh.ws) return;

  const socket = new WebSocket(
    api.buildWsUrl(`/ws/vms/status?host=${encodeURIComponent(rh.host)}`)
  );
  rh.ws = socket;
  let opened = false;

  socket.onopen = () => {
    opened = true;
    rh.reconnectDelay = RECONNECT_DELAY_INITIAL;
  };

  socket.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'connected') {
        // Proxy connection to remote host established — subscribe now.
        // Use remote_id values since the remote vmsmand only knows its
        // own local IDs.
        const remoteIds = vms
          .filter(vm => vm.host === rh.host && vm.remote_id != null && vm.remote_id >= 0)
          .map(vm => vm.remote_id!);
        if (remoteIds.length > 0) {
          socket.send(JSON.stringify({ type: 'subscribe', vm_ids: remoteIds }));
        } else {
          // No known remote IDs — subscribe to all and rely on ID mapping
          socket.send(JSON.stringify({ type: 'subscribe', all: true }));
        }
      } else if (data.type === 'vm_status' && statusHandler) {
        // Tag with the host this update came from so the handler can
        // verify the VM is actually delegated to this host.
        data.host = rh.host;

        // Map the remote host's VM ID to our local VM ID.
        // The remote host sends its own local ID in data.id, but locally
        // we track the mapping via remote_id on each delegated VM.
        // Fall back to name matching when remote_id isn't set yet (newly
        // delegated VMs before the first proxy request assigns one).
        const remoteId = data.id as number | undefined;
        const localVm = (remoteId != null
          && vms.find(vm => vm.host === rh.host && vm.remote_id === remoteId))
          || (data.name && vms.find(vm => vm.host === rh.host && vm.name === data.name));

        if (localVm) {
          data.id = localVm.id;

          // Capture remote_id from this mapping so future lookups work
          // even before the next REST fetch propagates it.
          if (remoteId != null && localVm.remote_id == null) {
            localVm.remote_id = remoteId;
          }
        } else {
          // No local VM matches — drop to avoid updating the wrong VM
          return;
        }

        statusHandler(data as WsVmStatus);
      }
    } catch {
      // Ignore parse errors
    }
  };

  socket.onclose = () => {
    rh.ws = null;
    if (!stopped) {
      if (!opened) {
        // WS upgrade failed — verify session before retrying
        api.verifySession().then(valid => {
          if (valid) {
            rh.reconnectTimer = setTimeout(() => {
              rh.reconnectTimer = null;
              connectRemoteHost(rh);
            }, rh.reconnectDelay);
            rh.reconnectDelay = Math.min(rh.reconnectDelay * 2, RECONNECT_DELAY_MAX);
          }
        });
      } else {
        rh.reconnectTimer = setTimeout(() => {
          rh.reconnectTimer = null;
          connectRemoteHost(rh);
        }, rh.reconnectDelay);
        rh.reconnectDelay = Math.min(rh.reconnectDelay * 2, RECONNECT_DELAY_MAX);
      }
    }
  };

  socket.onerror = () => {
    // onclose fires after onerror
  };
}

/**
 * Open a status WebSocket per remote host, reusing the same status handler.
 * Call after connectVmStatus() once the VM list is known.
 */
export function connectRemoteHostStatus(hosts: string[]): void {
  disconnectRemoteHosts();
  for (const host of hosts) {
    const rh: RemoteHostConn = {
      host,
      ws: null,
      reconnectTimer: null,
      reconnectDelay: RECONNECT_DELAY_INITIAL,
    };
    remoteHosts.push(rh);
    connectRemoteHost(rh);
  }
}

function disconnectRemoteHosts(): void {
  for (const rh of remoteHosts) {
    if (rh.reconnectTimer) {
      clearTimeout(rh.reconnectTimer);
      rh.reconnectTimer = null;
    }
    if (rh.ws) {
      const old = rh.ws;
      rh.ws = null;
      if (old.readyState === WebSocket.OPEN || old.readyState === WebSocket.CONNECTING) {
        old.close();
      }
    }
  }
  remoteHosts.length = 0;
}
